import React, { useCallback, useContext, useMemo } from "react";
import {
  TextButton,
  IconButton,
  ActionsMenu,
  useMutationWrapper,
  ModalContext,
  showDialog,
  showErrorDialog,
} from "@SeemplicityDev/shared";
import AllowedDomainsModal from "./AllowedDomainsModal/AllowedDomainsModal";
import {
  CNC_DISCONNECT_SAML,
  type CncDisconnectSamlData,
  type CncDisconnectSamlVariables,
} from "./CustomerProvisioningApi";
import { showToast } from "../Forms/Dialogs/Toast/Toast";
import NewAccount from "./NewAccount/NewAccount";
import DeleteOrganizationModal from "./DeleteOrganizationModal/DeleteOrganizationModal";

interface OrganizationActionsProps {
  organizationSlug: string;
  organizationName: string;
  domains: string[] | null;
  enabled: boolean;
  compact?: boolean;
  canDelete?: boolean;
}

// Minimal shape for ActionsMenu items; the shared package does not re-export
// its ActionsMenuItem type from the barrel, so we describe the fields we use.
interface OrganizationActionMenuItem {
  component: React.ReactNode;
  onClick: () => void;
}

const OrganizationActions: React.FC<OrganizationActionsProps> = ({
  organizationSlug,
  organizationName,
  domains,
  enabled,
  compact,
  canDelete,
}) => {
  const { openModal, closeModal } = useContext(ModalContext);

  const [disconnectSAML] = useMutationWrapper<
    CncDisconnectSamlData,
    CncDisconnectSamlVariables
  >(CNC_DISCONNECT_SAML, {
    refetchQueries: ["cnc_users"],
  });

  const newAccountClicked = useCallback(() => {
    openModal({
      title: `New Account for ${organizationName}`,
      size: "xs",
      overlay: true,
      component: (
        <NewAccount
          organizationSlug={organizationSlug}
          organizationName={organizationName}
        />
      ),
    });
  }, [openModal, organizationName, organizationSlug]);

  const onDisconnectSAMLClicked = useCallback(() => {
    disconnectSAML({
      variables: {
        organization_slug: organizationSlug,
      },
    })
      .then((res) => {
        if (!!res.data?.cnc_disconnect_saml?.ok) {
          closeModal();
          showToast({
            message: res.data?.cnc_disconnect_saml?.msg,
          });
        } else {
          showErrorDialog({ message: res.data?.cnc_disconnect_saml?.msg });
        }
      })
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      });
  }, [disconnectSAML, closeModal, organizationSlug]);

  const menuItems = useMemo(() => {
    const items: OrganizationActionMenuItem[] = [
      {
        component: <span>Domains</span>,
        onClick: () => {
          openModal({
            title: `Allowed Domains - ${organizationName}`,
            size: "m",
            overlay: true,
            component: (
              <AllowedDomainsModal
                organizationSlug={organizationSlug}
                domains={domains as string[]}
              />
            ),
          });
        },
      },
      {
        component: <span>Disconnect SAML</span>,
        onClick: () => {
          showDialog({
            title: "Disconnect SAML",
            message: `Are you sure you want to disconnect SAML for ${organizationName}?`,
            buttons: [
              {
                label: "Cancel",
              },
              {
                "data-testid": "disconnect-customer-confirm",
                buttonStyle: "danger",
                label: "Disconnect",
                onClick: () => {
                  onDisconnectSAMLClicked();
                },
              },
            ],
          });
        },
      },
    ];
    if (compact && enabled) {
      items.unshift({
        component: (
          <span style={{ color: "var(--primary-theme-color)" }}>
            New Account
          </span>
        ),
        onClick: newAccountClicked,
      });
    }
    if (canDelete) {
      items.push({
        component: (
          <span style={{ color: "var(--critical-alert-bg-color)" }}>
            Delete Organization
          </span>
        ),
        onClick: () => {
          openModal({
            title: "Delete Organization",
            size: "s",
            overlay: true,
            component: (
              <DeleteOrganizationModal
                organizationSlug={organizationSlug}
                organizationName={organizationName}
              />
            ),
          });
        },
      });
    }
    return items;
  }, [
    organizationSlug,
    organizationName,
    domains,
    compact,
    canDelete,
    newAccountClicked,
    onDisconnectSAMLClicked,
    openModal,
  ]);

  return (
    <>
      {!compact && enabled && (
        <>
          <TextButton
            data-testid={`${organizationSlug}-new-account`}
            label={"+ New Account"}
            buttonStyle={"primary"}
            size={"small"}
            disabled={!enabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              newAccountClicked();
            }}
          />
          <div className={"vertical-separator"} />
        </>
      )}
      <ActionsMenu
        data-testid="organizations-action-menu"
        menuItems={menuItems}
      >
        <IconButton
          data-testid="cnc-more-actions-menu"
          isIcon="seem-icon-more-actions"
          className="rules-table-actions-button"
        />
      </ActionsMenu>
    </>
  );
};

export default OrganizationActions;
